import { useCallback, useState } from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { Stack, useLocalSearchParams } from 'expo-router';

import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { generateAPIUrl } from '@/utils';

type Status = 'idle' | 'submitting' | 'done' | 'error';

/**
 * Public landing page for the account-deletion confirmation email link.
 *
 * Reads ?token=... from the URL, shows a Confirm button, and on tap calls
 * POST /api/account/delete-confirm. Server validates the token and performs
 * the cascading auth.users delete.
 *
 * No auth required to view this page — the token itself is the proof of intent.
 */
export default function ConfirmDeleteScreen() {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const params = useLocalSearchParams<{ token?: string }>();
  const token = typeof params.token === 'string' ? params.token : null;

  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleConfirm = useCallback(async () => {
    if (!token) return;
    setStatus('submitting');
    setErrorMessage(null);

    try {
      const response = await fetch(generateAPIUrl('/api/account/delete-confirm'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });

      if (!response.ok) {
        let message = 'Failed to confirm deletion';
        try {
          const payload = (await response.json()) as { error?: string };
          if (payload.error) message = payload.error;
        } catch {
          // ignore parse failure
        }
        setErrorMessage(message);
        setStatus('error');
        return;
      }
      setStatus('done');
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Network error');
      setStatus('error');
    }
  }, [token]);

  const renderBody = () => {
    if (!token) {
      return (
        <Text style={[styles.body, { color: palette.danger }]}>
          This link is missing a token. Please use the link from your email.
        </Text>
      );
    }

    if (status === 'done') {
      return (
        <>
          <Text style={[styles.title, { color: palette.text }]}>Account deleted</Text>
          <Text style={[styles.body, { color: palette.textMuted }]}>
            Your FimFam account and stored data have been removed. You can
            close this page.
          </Text>
          <Text style={[styles.body, { color: palette.textMuted }]}>
            Your calendar events were not touched — they remain in your
            device&apos;s native calendar (Google Calendar, iCloud, etc.)
            exactly as they were.
          </Text>
        </>
      );
    }

    return (
      <>
        <Text style={[styles.title, { color: palette.text }]}>Confirm account deletion</Text>
        <Text style={[styles.body, { color: palette.textMuted }]}>
          Tapping Confirm permanently deletes your FimFam account and every
          record we&apos;ve stored about you — profile, tasks, AI summaries.
          This cannot be undone.
        </Text>
        <Text style={[styles.body, { color: palette.textMuted }]}>
          Your calendar events are <Text style={{ fontWeight: '600' }}>not</Text> deleted.
          FimFam reads from your device&apos;s native calendar accounts (Google
          Calendar, iCloud, etc.) and never owns those events — they stay in
          your calendar after deletion. FimFam simply loses access to them.
        </Text>

        {errorMessage && (
          <Text style={[styles.error, { color: palette.danger }]}>{errorMessage}</Text>
        )}

        <TouchableOpacity
          accessibilityRole="button"
          onPress={handleConfirm}
          disabled={status === 'submitting'}
          style={[
            styles.button,
            {
              backgroundColor: palette.danger,
              opacity: status === 'submitting' ? 0.6 : 1,
            },
          ]}
        >
          {status === 'submitting' ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <Text style={styles.buttonText}>Confirm deletion</Text>
          )}
        </TouchableOpacity>
      </>
    );
  };

  return (
    <View style={[styles.container, { backgroundColor: palette.background }]}>
      <Stack.Screen options={{ title: 'Confirm deletion', headerShown: false }} />
      <View style={[styles.card, { backgroundColor: palette.cardBackground }]}>
        {renderBody()}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: Spacing.lg,
  },
  card: {
    width: '100%',
    maxWidth: 420,
    padding: Spacing.xl,
    borderRadius: BorderRadius.lg,
    gap: Spacing.md,
  },
  title: { fontSize: 20, fontWeight: '600' },
  body: { fontSize: 15, lineHeight: 22 },
  error: { fontSize: 14 },
  button: {
    paddingVertical: Spacing.md,
    borderRadius: BorderRadius.md,
    alignItems: 'center',
    marginTop: Spacing.sm,
  },
  buttonText: { color: '#fff', fontSize: 16, fontWeight: '600' },
});
